import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import Seo from '../components/Seo';
import './SupportPage.css';

const QUICK_LINKS = [
  { icon: '💳', label: 'Manage subscription',  desc: 'Cancel, reactivate, or view billing',  to: '/account' },
  { icon: '💰', label: 'Refund policy',         desc: 'When refunds are available',           to: '/refunds' },
  { icon: '✉️', label: 'Contact us',            desc: 'Send a message or report a bug',       to: '/contact' },
  { icon: '📖', label: 'Pricing',               desc: 'Compare free vs Premium features',     to: '/pricing' },
];

const FAQS = [
  {
    category: 'Account & Billing',
    items: [
      {
        q: 'How do I cancel my subscription?',
        a: 'Go to your Account page and click "Cancel subscription." You keep Premium access until the end of your current billing period. No further charges will be made.',
      },
      {
        q: 'How do I reactivate a cancelled subscription?',
        a: 'If you cancelled but your billing period hasn\'t ended yet, go to your Account page and click "Reactivate subscription." Your subscription will continue as normal.',
      },
      {
        q: 'Can I get a refund?',
        a: 'All subscriptions are generally non-refundable since access is granted immediately. Exceptions include billing errors, duplicate charges, or technical issues that prevented access. See our Refund Policy for full details.',
      },
      {
        q: 'What payment methods are accepted?',
        a: 'We accept all major credit and debit cards (Visa, Mastercard, Amex, Discover) via Stripe. We do not store your card details.',
      },
      {
        q: 'How do I delete my account?',
        a: 'Go to your Account page and scroll to the bottom. Click "Delete account." This will immediately cancel any active subscription and permanently remove your account.',
      },
      {
        q: 'I forgot my password. How do I reset it?',
        a: 'On the sign-in screen, click "Forgot password?" and enter your email. You\'ll receive a reset link within a few minutes. Check your spam folder if it doesn\'t arrive.',
      },
    ],
  },
  {
    category: 'Premium Features',
    items: [
      {
        q: 'What do I get with Premium?',
        a: 'Premium unlocks Trends Analysis, Distribution & Stats, Number Pairs, Gap Analysis, Smart Picker modes (weighted and overdue), and Backtest My Numbers — everything under the Analytics and Tools tabs.',
      },
      {
        q: 'What is Backtest My Numbers?',
        a: 'Enter the numbers you regularly play and see how they would have performed across every historical draw — match counts by tier, theoretical winnings vs. spend, and a profile of each number\'s hot/cold status.',
      },
      {
        q: 'What are Smart Picker modes?',
        a: 'The Picker tab offers three modes: Random (equal probability), Weighted (numbers drawn more often appear more often in the pool), and Overdue (draws from numbers with the longest gap since last appearance).',
      },
      {
        q: 'Does Premium predict winning numbers?',
        a: 'No. Lottery draws are mathematically independent random events — past frequency has no bearing on future results. Premium gives you richer data analysis tools for entertainment and exploration, not a system for winning.',
      },
    ],
  },
  {
    category: 'Data & Accuracy',
    items: [
      {
        q: 'Where does the draw data come from?',
        a: 'All Powerball and Mega Millions data comes directly from the New York State government open data portal (data.ny.gov), which is updated after every draw.',
      },
      {
        q: 'How current is the data?',
        a: 'The app fetches data live from the NY open data API each time you load it, so you\'re always seeing the latest available draws.',
      },
      {
        q: 'Why are some draws missing?',
        a: 'Occasionally there is a delay between a draw occurring and the data appearing on the government portal. If a very recent draw is missing, check back in a few hours.',
      },
      {
        q: 'What date range filters do?',
        a: 'The date range buttons (All time, 5 yr, 3 yr, 1 yr) filter all analysis to only include draws within that window. This lets you see if patterns have shifted in recent years vs. the full history.',
      },
    ],
  },
  {
    category: 'Technical',
    items: [
      {
        q: 'The app isn\'t loading data. What should I do?',
        a: 'First, check your internet connection. The app fetches live data from a government API — if that service is temporarily down, data won\'t load. Try refreshing in a few minutes. If the issue persists, contact us.',
      },
      {
        q: 'Charts look broken or cut off on my phone.',
        a: 'Try rotating your device to landscape mode for wider charts. If the issue persists on a specific tab, let us know via the contact form with your device and browser details.',
      },
      {
        q: 'I subscribed but the app still shows free features.',
        a: 'Try signing out and signing back in. If that doesn\'t work, hard-refresh the page (Cmd+Shift+R on Mac, Ctrl+Shift+R on Windows). If the issue continues, contact us and we\'ll sort it out.',
      },
    ],
  },
];

function FaqItem({ q, a }) {
  const [open, setOpen] = useState(false);
  return (
    <div className={`sp-faq-item ${open ? 'open' : ''}`}>
      <button className="sp-faq-q" onClick={() => setOpen(o => !o)}>
        <span>{q}</span>
        <span className="sp-faq-chevron">{open ? '−' : '+'}</span>
      </button>
      {open && <p className="sp-faq-a">{a}</p>}
    </div>
  );
}

export default function SupportPage() {
  return (
    <div className="sp-page">
      <Seo
        path="/support"
        title="Support & FAQ — LottoPickle"
        description="Answers to common questions about LottoPickle subscriptions, features, and data sources, plus how to get in touch with our team."
      />
      <nav className="sp-nav">
        <Link to="/" className="sp-nav-logo">Lotto<span>Pickle</span></Link>
        <Link to="/app" className="sp-nav-back">&larr; Back to app</Link>
      </nav>

      <div className="sp-content">
        <div className="sp-hero">
          <h1>Support</h1>
          <p>Find answers, manage your account, or get in touch.</p>
        </div>

        {/* Quick action cards */}
        <div className="sp-quick-grid">
          {QUICK_LINKS.map(l => (
            <Link key={l.to} to={l.to} className="sp-quick-card">
              <span className="sp-quick-icon">{l.icon}</span>
              <span className="sp-quick-label">{l.label}</span>
              <span className="sp-quick-desc">{l.desc}</span>
            </Link>
          ))}
        </div>

        {/* FAQ sections */}
        <div className="sp-faqs">
          {FAQS.map(section => (
            <div key={section.category} className="sp-faq-section">
              <h2 className="sp-faq-category">{section.category}</h2>
              {section.items.map(item => (
                <FaqItem key={item.q} q={item.q} a={item.a} />
              ))}
            </div>
          ))}
        </div>

        {/* Still need help */}
        <div className="sp-contact-banner">
          <div className="sp-contact-banner-text">
            <h3>Still need help?</h3>
            <p>We usually respond within 1–2 business days.</p>
          </div>
          <div className="sp-contact-banner-actions">
            <Link to="/contact" className="sp-contact-btn sp-contact-btn-primary">Send a message</Link>
            <a href="mailto:support@lottopickle.com" className="sp-contact-btn sp-contact-btn-secondary">support@lottopickle.com</a>
          </div>
        </div>
      </div>

      <footer className="sp-footer">
        <div className="sp-footer-links">
          <Link to="/">Home</Link>
          <Link to="/app">App</Link>
          <Link to="/pricing">Pricing</Link>
          <Link to="/privacy">Privacy</Link>
          <Link to="/terms">Terms</Link>
          <Link to="/refunds">Refunds</Link>
          <Link to="/contact">Contact</Link>
        </div>
        <p className="sp-footer-note">For entertainment and data analysis purposes only. Not gambling advice.</p>
      </footer>
    </div>
  );
}
